<template>
    <div
        ref="chartTableWrapper"
        class="chart-table-wrapper"
        :class="{ 'no-header': !hasHeader }"
        @mouseenter="editOptionsRef?.showSettings($event)"
        @focusin="editOptionsRef?.showSettings($event)"
        @mouseleave="editOptionsRef?.hideSettings()"
        @focusout="editOptionsRef?.hideSettings()"
    >
        <conditional-teleport :to="chartPopoutRef">
            <module-footer
                ref="editOptionsRef"
                :module-name="props.title ?? ''"
                :blocked-actions="footerBlockedActions"
                :has-left-options="!props.hideChartOptions"
                display-options-top
                @fullscreen:module="showFullScreen"
                @pop-out:module="openChartPopout"
            >
                <template #left>
                    <div class="chart-options">
                        <div ref="chartButtonsLeft" />
                        <action-button
                            v-if="!showTable"
                            class="icon-controls"
                            title="Download chart as csv"
                            :icon="['fas', 'download']"
                            show-title-on-touch
                            @click="exportChartCSV"
                            :isDisabled="props.disableCsvExport"
                        />
                        <action-button
                            v-if="!props.hideTable"
                            class="icon-controls"
                            :title="chartTableTooltip"
                            :icon="['fas', chartTableIcon]"
                            show-title-on-touch
                            @click="changeTableOrChart"
                        />
                        <action-button
                            v-if="!showTable"
                            class="icon-controls"
                            title="Download PNG chart"
                            @click="downloadChart"
                            :icon="['far', 'image']"
                            show-title-on-touch
                        />
                    </div>
                </template>
            </module-footer>
            <div v-if="hasHeader" class="header-row" :class="{ 'margin-below': showTable }">
                <slot v-if="hasCustomHeader" name="header" />
                <h5 v-else-if="props.title">{{ props.title }}</h5>
            </div>
            <generic-table
                v-if="showTable"
                ref="genericTableRef"
                :data="tableData"
                :options-teleport="chartButtonsLeft"
                :filename="props.csvTitle || props.title || 'chart'"
                :prevent-download="props.disableCsvExport || props.hideChartOptions"
                :cannot-edit="props.hideTableEditButton"
                :hide-top-filters="props.hideChartOptions && props.hideTableEditButton"
                row-click-mode="disabled"
            />
            <div v-else class="chart-cell">
                <base-chart
                    constructor-type='chart'
                    :initialOptions="chartOptionsWithZoom"
                    :full-screen-count="fullScreenCount"
                    :download-chart-count="downloadChartCount"
                    :key="chartKey"
                    transparent-bg
                    ref="baseChartRef"
                />
            </div>
        </conditional-teleport>
        <central-modal
            v-model:visible="showChartPopout"
            :header="props.title || 'Expanded Chart'"
            size="medium"
        >
            <div
                ref="chartPopoutRef"
                class="chart-table-wrapper expanded"
                :class="{ 'no-header': !hasHeader }"
                @mouseenter="editOptionsRef?.showSettings($event)"
                @focusin="editOptionsRef?.showSettings($event)"
                @mouseleave="editOptionsRef?.hideSettings()"
                @focusout="editOptionsRef?.hideSettings()"
            />
        </central-modal>
    </div>
</template>

<script lang="ts" setup>
import {
    Options, PointOptionsObject, Series, SeriesAreaOptions, SeriesBarOptions, SeriesColumnOptions, SeriesGaugeOptions, SeriesLineOptions, SeriesPieOptions, SeriesTimelineOptions,
    YAxisOptions,
} from 'highcharts';
import {
    computed, onMounted, onUnmounted, useTemplateRef, ref, Slots, useSlots, watch, nextTick,
} from 'vue';
import { exportCsv } from '@/utils/CsvExport.ts';
import ActionButton from '@/components/powerMarkets/common/ActionButton.vue';
import { buildGenericTableData } from '@/helpers/table/genericTableData.ts';
import {
    useChartSync, useYAxisSync, useLegendSync, computeSharedYAxisExtents,
} from '@/composables/powerMarkets/useChartCommunication.ts';
import { getSyncTooltipHover } from '@/helpers/charts/chartSettings.ts';
import type { IDashboardOverrides } from '@/interfaces/powerMarkets/dashboard/IDashboard.ts';
import { CANNOT_POPOUT_BLOCKED_ACTIONS, IModuleBlockedActions, VIEW_ONLY_CHART_BLOCKED_ACTIONS } from '@/interfaces/actions/enact/IModuleActions.ts';
import BaseChart from './BaseChart.vue';
import ConditionalTeleport from '../layout/ConditionalTeleport.vue';
import GenericTable from '../powerMarkets/tables/GenericTable.vue';
import CentralModal from '../common/CentralModal.vue';
import ModuleFooter from '../common/ModuleFooter.vue';

const props = defineProps<{
    title?: string;
    csvTitle?: string;
    xAxisCategory?: string;
    chartCategories: Array<string>;
    chartOptions: Options;
    prefixUnit?: string | null;
    suffixUnit?: string | null;
    additionalCsvExport?: Array<Array<string | number | undefined>>;
    optionsTeleport?: HTMLElement | null;
    hideTable?: boolean;
    disableCsvExport?: boolean;
    hideChartOptions?: boolean;
    hideTableEditButton?: boolean;
    dateOverrides?: IDashboardOverrides | null;
    yAxisNormalizationGroupKey?: string | number;
    disableSharedTooltipSync?: boolean;
}>();

const footerBlockedActions = computed<IModuleBlockedActions>(() => (showChartPopout.value
    ? { ...VIEW_ONLY_CHART_BLOCKED_ACTIONS, ...CANNOT_POPOUT_BLOCKED_ACTIONS }
    : VIEW_ONLY_CHART_BLOCKED_ACTIONS));

const emit = defineEmits<{
    'is-table': [boolean];
}>();

const slots: Slots = useSlots();
const editOptionsRef = useTemplateRef('editOptionsRef');

const chartPopoutRef = ref<HTMLElement | null>(null);
const chartTableWrapper = ref<HTMLElement | null>(null);
const chartButtonsLeft = ref<HTMLElement | null>(null);
const genericTableRef = ref<InstanceType<typeof GenericTable> | null>(null);

const chartKey = ref<number>(0);
let resizeAnimationFrame: number | null = null;
const resizeObserver = ref<ResizeObserver | null>(null);

const isFullScreen = ref<boolean>(false);
const showTable = ref<boolean>(false);
const showChartPopout = ref<boolean>(false);

const baseChartRef = ref<typeof BaseChart | null>(null);

const chartOptionsWithZoom = ref<Options>({});

const chartSync = useChartSync();
const yAxisSync = useYAxisSync(computed(() => props.yAxisNormalizationGroupKey));
const legendSync = useLegendSync();

const downloadChartCount = ref<number>(0);
const fullScreenCount = ref<number>(0);
const hasCustomHeader = computed<boolean>(() => !!slots.header);
const hasHeader = computed<boolean>(() => (hasCustomHeader.value || !!props.title));
const chartTableIcon = computed<string>(() => (showTable.value ? 'chart-line' : 'table'));
const chartTableTooltip = computed<string>(() => (showTable.value ? 'Show data as chart' : 'Show data as table'));

const chartData = computed(() => props.chartOptions.series as Array<SeriesColumnOptions | SeriesAreaOptions | SeriesPieOptions | SeriesBarOptions | SeriesGaugeOptions | SeriesLineOptions | SeriesTimelineOptions> ?? []);

const tableData = computed(() => {
    if (chartData.value.length === 0) return buildGenericTableData([], []);

    return buildGenericTableData([
        { id: 'category', title: props.xAxisCategory ?? 'Category' },
        ...chartData.value.map((series, index) => ({
            id: `series-${index}`,
            title: series.name ?? `Series ${index}`,
            prefix: props.prefixUnit,
            suffix: props.suffixUnit,
        })),
    ], props.chartCategories.map((category, index) => [
        category,
        ...chartData.value.map(series => getProcessedPoint(series.data?.[index] as PointOptionsObject | number | null | undefined)),
    ]));
});

function changeTableOrChart (): void {
    showTable.value = !showTable.value;
    emit('is-table', showTable.value);
}

function getProcessedPoint (rawObj: PointOptionsObject | number | null | undefined): string | number | null | undefined {
    if (!rawObj && typeof rawObj !== 'number') return rawObj;
    return (typeof rawObj === 'number') ? rawObj : rawObj.y ?? null;
}

function exportChartCSV (): void {
    if (props.disableCsvExport) return;
    if (chartData.value.length === 0) return;
    if (props.chartCategories === undefined) return;

    const data: Array<Array<string | number | undefined>> = [];

    data.push([props.xAxisCategory ?? '']);
    props.chartCategories.forEach(category => {
        data.push([category]);
    });

    chartData.value.forEach(series => {
        data[0].push(series.name ?? '');

        for (let i = 0; i < props.chartCategories!.length; i += 1) {
            if (series.data) {
                const seriesOption = series.data as PointOptionsObject[];
                data[i + 1].push(getProcessedPoint(seriesOption[i]) ?? '-');
            }
        }
    });

    if (props.additionalCsvExport) {
        data.push([]);
        data.push(...props.additionalCsvExport);
    }

    data[0].forEach((x, i) => {
        if (i === 0) return;
        const prefix = props.prefixUnit ?? '';
        const suffix = props.suffixUnit ?? '';
        if (prefix !== '' || suffix !== '') data[0][i] = `${x} (${prefix}${suffix})`;
    });
    exportCsv(data ?? [[]], props.csvTitle || props.title || 'chart');
}

function openTableDownload (event: Event): void {
    genericTableRef.value?.openDownloadTableOverlay(event);
}

function openTableEdit (event: Event): void {
    genericTableRef.value?.showEditTableOverlay(event);
}

function openChartPopout (): void {
    showChartPopout.value = true;
}

function downloadChart (): void {
    baseChartRef.value?.exportChart();
}

function showFullScreen (): void {
    isFullScreen.value = true;
    baseChartRef.value?.fullscreen();
}

function clearYAxisMinMaxForAutoScale (yAxis: Options['yAxis']): Options['yAxis'] {
    if (!yAxis) return yAxis;
    const clearOne = (axis: YAxisOptions): YAxisOptions => ({
        ...axis,
        min: null,
        max: null,
    });
    if (!Array.isArray(yAxis)) {
        return clearOne(yAxis as YAxisOptions);
    }
    return yAxis.map((axis) => clearOne(axis as YAxisOptions));
}

function applySharedYAxisRange (): void {
    if (!props.dateOverrides?.commonSettings?.useYAxisNormalisation || !chartOptionsWithZoom.value?.yAxis) return;
    const options = chartOptionsWithZoom.value;

    if (!yAxisSync.sharedYAxisRange.value) {
        if (!Array.isArray(options.yAxis)) {
            options.yAxis = { ...options.yAxis, min: undefined, max: undefined };
        } else {
            options.yAxis = options.yAxis.map((yAxis) => ({ ...yAxis, min: undefined, max: undefined }));
        }
        chartOptionsWithZoom.value = { ...options };
        baseChartRef.value?.updateOptions(chartOptionsWithZoom.value);
        return;
    }

    const { min, max } = yAxisSync.sharedYAxisRange.value;
    if (!Array.isArray(options.yAxis)) {
        options.yAxis = { ...options.yAxis, min, max };
    } else {
        options.yAxis = options.yAxis.map((yAxis) => ({ ...yAxis, min, max }));
    }
    chartOptionsWithZoom.value = { ...options };
    baseChartRef.value?.updateOptions(chartOptionsWithZoom.value);
}

function setChartOptionsWithZoom (): void {
    if (!props.chartOptions) return;
    let options: Options = {
        ...props.chartOptions,
        chart: {
            ...props.chartOptions.chart,
            zooming: {
                type: 'x',
                resetButton: {
                    position: {
                        x: 0,
                        y: 0,
                    },
                    theme: {
                        r: 4,
                        padding: 8,
                        stroke: 'var(--neutral300)',
                        'stroke-width': 1,
                        fill: 'var(--neutral300)',
                        style: {
                            color: 'var(--primaryFont)',
                            fontSize: 'var(--smallFontSize)',
                        },
                        states: {
                            hover: {
                                fill: 'var(--neutral400)',
                                stroke: 'var(--neutral400)',
                            },
                        },
                    },
                },
            },
        },
    };

    const useSharedTooltip = props.dateOverrides?.commonSettings?.sharedTooltipAcrossModules && !props.disableSharedTooltipSync;
    if (useSharedTooltip || props.dateOverrides?.commonSettings?.sharedLegendToggling) {
        const seriesOpts = { ...options.plotOptions?.series };
        if (useSharedTooltip) {
            (seriesOpts as Record<string, unknown>).point = getSyncTooltipHover((point: { x?: number }) => point.x);
        }
        if (props.dateOverrides?.commonSettings?.sharedLegendToggling) {
            (seriesOpts as Record<string, unknown>).events = {
                ...(options.plotOptions?.series as { events?: Record<string, () => boolean> })?.events,
                legendItemClick (this: Series): boolean {
                    const userOptions = this.userOptions as { id?: string };
                    if (userOptions?.id) {
                        legendSync.setSeriesVisibility(userOptions.id, this.visible);
                    }
                    return true;
                },
            };
        }
        options = {
            ...options,
            plotOptions: {
                ...options.plotOptions,
                series: seriesOpts,
            },
        };
    }

    if (props.dateOverrides?.commonSettings?.sharedLegendToggling && options.series) {
        options = {
            ...options,
            series: options.series.map((s, i) => ({
                ...s,
                id: (s as SeriesLineOptions).id ?? (s as SeriesLineOptions).name ?? `series-${i}`,
            })),
        };
    }

    if (props.dateOverrides?.commonSettings?.useYAxisNormalisation === false) {
        options.yAxis = clearYAxisMinMaxForAutoScale(options.yAxis);
        yAxisSync.setSharedYAxisRange(null);
    }

    chartOptionsWithZoom.value = options;

    if (props.dateOverrides?.commonSettings?.useYAxisNormalisation) {
        const extent = computeSharedYAxisExtents(
            props.chartOptions.plotOptions,
            props.chartOptions.series ?? [],
            typeof props.chartOptions.chart?.type === 'string' ? props.chartOptions.chart.type : undefined,
        );
        if (extent != null) {
            yAxisSync.updateYAxisRange(extent.min, extent.max);
        }
        applySharedYAxisRange();
    }
}

// BaseChart consumes initialOptions on mount, so prepare them during setup.
setChartOptionsWithZoom();

function scheduleChartResize (): void {
    if (isFullScreen.value || resizeAnimationFrame !== null) return;

    resizeAnimationFrame = requestAnimationFrame(() => {
        resizeAnimationFrame = null;
        baseChartRef.value?.resize();
    });
}

watch(() => props.chartOptions, () => {
    setChartOptionsWithZoom();
    baseChartRef.value?.updateOptions(chartOptionsWithZoom.value);
});

watch(() => yAxisSync.sharedYAxisRange.value, (range) => {
    if (!props.dateOverrides?.commonSettings?.useYAxisNormalisation) return;
    applySharedYAxisRange();
    if (range == null) {
        nextTick(() => {
            setChartOptionsWithZoom();
        });
    }
}, { deep: true });

/** chart.update() does not fully drop fixed Y extremes; remount so each chart rescales to its own data. */
watch(() => props.dateOverrides?.commonSettings?.useYAxisNormalisation, (useNorm, previous) => {
    if (previous === undefined) return;
    if (useNorm) return;
    setChartOptionsWithZoom();
    nextTick(() => {
        chartKey.value += 1;
    });
});

watch(() => chartSync.hoveredTimestamp.value, () => {
    if (props.dateOverrides?.commonSettings?.sharedTooltipAcrossModules && !props.disableSharedTooltipSync && baseChartRef.value) {
        baseChartRef.value.triggerTooltipHover(chartSync.hoveredTimestamp.value);
    }
});

watch(() => legendSync.sharedSeriesVisibility.value, (visibilityMap) => {
    if (!props.dateOverrides?.commonSettings?.sharedLegendToggling || !baseChartRef.value) return;
    baseChartRef.value.applySharedSeriesVisibility(visibilityMap);
}, { deep: true });

onMounted(() => {
    if (!chartTableWrapper.value) return;
    resizeObserver.value = new ResizeObserver(scheduleChartResize);
    resizeObserver.value.observe(chartTableWrapper.value);
});

onUnmounted(() => {
    if (resizeObserver.value) resizeObserver.value.disconnect();
    if (resizeAnimationFrame !== null) {
        cancelAnimationFrame(resizeAnimationFrame);
        resizeAnimationFrame = null;
    }
});

defineExpose({
    changeTableOrChart,
    exportChartCSV,
    openTableDownload,
    openTableEdit,
    showTable,
});
</script>

<style lang="scss" scoped>
.chart-table-wrapper {
    position: relative;
    display: grid;
    grid-template-rows: auto minmax(0, 1fr);
    overflow: hidden;
    height: 100%;
    width: 100%;

    &.expanded {
        padding: var(--defaultSpacing);
    }

    &.no-header {
        grid-template-rows: minmax(0, 1fr);
    }

    .chart-cell {
        min-width: 0;
        min-height: 0;
    }

    .header-row {
        display: flex;
        align-items: center;
        justify-content: center;

        &.margin-below {
            margin-bottom: var(--defaultSpacing);
        }
    }
}

.chart-options {
    display: flex;
    justify-content: space-between;
    width: 100%;
    pointer-events: auto;
}

.chart-table-popout {
    overflow: hidden;
    .chart-table-wrapper {
        width: 80vw;
        height: 80vh;
        max-width: 2000px;
        max-height: 2000px;
    }
}
</style>
